import React, { useState, useEffect } from 'react';
import {
  Box, Stack, Typography, TextField, MenuItem, Select, InputLabel,
  FormControl, Switch, FormControlLabel, Divider, Button
} from '@mui/material';
import dayjs from 'dayjs';
import TariffService from '../TariffService';
import RatingService from '../RatingService';
import DecimalInput from '../UiComponents/DecimalInput';
import SubMeterInputs from './SubMeterInputs'; // varsayılan konum

const InvoiceForm = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tariffId, setTariffId] = useState('');
  const [tariffs, setTariffs] = useState([]);
  const [indexTypes, setIndexTypes] = useState([]);
  const [mainMeterConsumptions, setMainMeterConsumptions] = useState({});
  const [contractPowerChargeSharing, setContractPowerChargeSharing] = useState(null);
  const [exceededPowerChargeSharing, setExceededPowerChargeSharing] = useState(null);
  const [subsidized, setSubsidized] = useState(true);
  const [subMeters, setSubMeters] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    TariffService.getTariffs().then(res => setTariffs(res.data));
  }, []);

  useEffect(() => {
    if (tariffId) {
      TariffService.getInvoiceIndexTypesByTariffId(tariffId).then(res => {
        setIndexTypes(res.data);
        setMainMeterConsumptions({});
      });
    }
  }, [tariffId]);

  const handleMainMeterInputChange = (type, value) => {
    setMainMeterConsumptions(prev => ({ ...prev, [type]: value }));
  };

  const handleSubMeterUpdate = (subMeterList) => {
    setSubMeters(subMeterList);
  };

  const buildRequest = () => {
    const priorityGroups = {};
    subMeters.forEach(meter => {
      if (!priorityGroups[meter.priority]) priorityGroups[meter.priority] = [];
      priorityGroups[meter.priority].push(meter);
    });

    const consumptionListByPriorityDtoos = Object.entries(priorityGroups).map(([priority, meters]) => {
      const indexGroups = {};
      meters.forEach(meter => {
        Object.entries(meter.consumptions).forEach(([type, value]) => {
          if (!indexGroups[type]) indexGroups[type] = [];
          indexGroups[type].push({ subMeterId: meter.subMeterId, consumption: parseFloat(value) });
        });
      });

      const consumptions = Object.entries(indexGroups).map(([type, shares]) => {
        const total = shares.reduce((sum, s) => sum + s.consumption, 0);
        return {
          indexType: type,
          consumption: priority === '1' ? parseFloat(mainMeterConsumptions[type] || 0) : total,
          subMeterShares: priority === '1' ? shares : shares.length > 0 ? shares : undefined
        };
      });

      return {
        priority: parseInt(priority),
        consumptions: consumptions.filter(c => c.consumption > 0)
      };
    });

    const basePriorityOne = {
      priority: 1,
      consumptions: indexTypes
        .map(it => ({
          indexType: it.indexType,
          consumption: parseFloat(mainMeterConsumptions[it.indexType] || 0),
          subMeterShares: []
        }))
        .filter(c => c.consumption > 0)
    };

    if (!subMeters.length && basePriorityOne.consumptions.length > 0) {
      consumptionListByPriorityDtoos.push(basePriorityOne);
    }

    return {
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
      tariffId,
      subsidized,
      contractPowerChargeSharing,
      exceededPowerChargeSharing,
      consumptionListByPriorityDtoos
    };
  };

  const handleCalculate = async () => {
    const req = buildRequest();
    const res = await RatingService.rating(req);
    setResult(res.data);
  };

  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>Fatura Tüketim Hesaplama</Typography>
      <Stack spacing={3}>

        <Stack direction="row" spacing={2}>
          <TextField
            label="Başlangıç"
            type="date"
            fullWidth
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Bitiş"
            type="date"
            fullWidth
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <FormControl fullWidth>
          <InputLabel>Tarife</InputLabel>
          <Select value={tariffId} onChange={(e) => setTariffId(e.target.value)}>
            {tariffs.map(t => <MenuItem key={t.tariffId} value={t.tariffId}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Switch checked={subsidized} onChange={e => setSubsidized(e.target.checked)} />}
          label="Sübvansiyon uygulanacak mı?"
        />

        {indexTypes.map(it => (
          <DecimalInput
            key={it.indexType}
            label={`Ana Sayaç - ${it.indexName}`}
            value={mainMeterConsumptions[it.indexType] || ''}
            onChange={(val) => handleMainMeterInputChange(it.indexType, val)}
            decimalSeparator="," maxDecimalDigits={3} unit={it.unit}
          />
        ))}

        <Divider />
        <Button onClick={handleCalculate} variant="contained">Fatura Tutarını Hesapla</Button>

        {result && (
          <Typography color="primary">
            Hesaplanan Toplam Tutar: {result.totalCharge} TL
          </Typography>
        )}

        <Divider sx={{ mt: 4 }} />
        <SubMeterInputs validIndexTypes={indexTypes.filter(i => !['P', 'D'].includes(i.indexType))} onChange={handleSubMeterUpdate} />

        {result?.subMeters?.length > 0 && (
          <>
            <Typography variant="h6" mt={2}>Paylaştırma Sonuçları</Typography>
            <pre style={{ background: '#f3f3f3', padding: '1em' }}>{JSON.stringify(result.subMeters, null, 2)}</pre>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default InvoiceForm;
